import { FC } from "react";

interface Props {
  /** Contracted ceiling. Null when there is no contract, or nobody has set one. */
  contractListSize: number | null;
  /** "CONTRACT" | "CAMPAIGN" | "" */
  sizedBy: string;
  /** The population this campaign actually has today. */
  actualSize: number;
}

/**
 * The contracted ceiling beside the real population (MPN-1718).
 *
 * Reference only. The ticket is explicit that exceeding this triggers nothing — no
 * block, no alert, no rejection — so the status reads as an observation rather than a
 * warning, and being over it is stated plainly instead of in error styling.
 *
 * Renders nothing when no ceiling is set. A contract nobody has sized is unknown, and
 * showing "0" or "—" beside a real population invites reading it as a limit of none.
 */
export const ContractListSizePanel: FC<Props> = ({ contractListSize, sizedBy, actualSize }) => {
  if (contractListSize == null) return null;

  const scope = sizedBy === "CAMPAIGN" ? "per campaign" : sizedBy === "CONTRACT" ? "per contract" : "";

  // A verdict is only honest when the ceiling applies to THIS campaign. A contract-sized
  // ceiling is a limit on the sum across every campaign under the contract, which this
  // screen cannot see -- comparing one campaign against it would call each of seven
  // campaigns "within" while the contract total sat far over. Unset is the same problem:
  // without knowing which it is, there is no comparison to make.
  const canJudge = sizedBy === "CAMPAIGN";
  const over = canJudge && actualSize > contractListSize;

  return (
    <div className="contractListSize mb-3">
      <div className="contractListSize__stat">
        <div className="contractListSize__label">Contract list size</div>
        <div className="contractListSize__value">
          {contractListSize.toLocaleString()}
          {scope && <span className="contractListSize__scope"> {scope}</span>}
        </div>
      </div>
      <div className="contractListSize__stat">
        <div className="contractListSize__label">Actual population size</div>
        <div className="contractListSize__value">{actualSize.toLocaleString()}</div>
      </div>
      {canJudge ? (
        <span className={`contractListSize__chip ${over ? "is-over" : "is-within"}`}>
          {over ? "Over contract limit" : "Within contract limit"}
        </span>
      ) : (
        <span className="contractListSize__chip is-neutral">
          {sizedBy === "CONTRACT"
            ? "Limit is across the contract"
            : "Sized by not set"}
        </span>
      )}
    </div>
  );
};
